import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaUser, FaEnvelope, FaIdCard, FaCalendar, FaClock, FaEdit, FaKey, FaCopy, FaCheck, FaTimes } from 'react-icons/fa';
import { getUserProfile, updateUserProfile, changePassword } from '../../api';
import styles from './UserProfile.module.css';

const UserProfile = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editForm, setEditForm] = useState({ username: '', email: '' });
  const [passwordForm, setPasswordForm] = useState({ 
    currentPassword: '', 
    newPassword: '', 
    confirmPassword: '' 
  });
  const [editLoading, setEditLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [copyStatus, setCopyStatus] = useState('idle');
  
  // Password visibility states
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        
        // First check if user data is already in localStorage
        const storedUserData = localStorage.getItem('userData');
        if (storedUserData) {
          try {
            const userData = JSON.parse(storedUserData);
            console.log('User profile data from localStorage:', userData);
            setUserProfile(userData);
            setError(null);
            setLoading(false);
            return;
          } catch (parseError) {
            console.error('Error parsing stored user data:', parseError);
          }
        }
        
        // Get user info from localStorage (set during login)
        const userEmail = localStorage.getItem('userEmail');
        const userRole = localStorage.getItem('userRole');
        
        if (!userEmail || !userRole) {
          // If no user info, use default demo user
          const defaultEmail = 'user@gmail.com';
          const defaultRole = 'user';
          const profile = await getUserProfile(defaultEmail, defaultRole);
          console.log('User profile data from API (default):', profile);
          setUserProfile(profile);
        } else {
          const profile = await getUserProfile(userEmail, userRole);
          console.log('User profile data from API:', profile);
          setUserProfile(profile);
        }
        
        setError(null);
      } catch (err) {
        setError('Failed to load user profile');
        console.error('Error fetching user profile:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'Not available';
    }
    
    try {
      const date = new Date(dateString);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return 'Not available';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Not available';
    }
  };

  const handleBack = () => {
    navigate('/user/dashboard');
  };

  const handleEditProfile = () => {
    setEditForm({
      username: userProfile.username,
      email: userProfile.email
    });
    setEditError('');
    setShowEditModal(true);
  };

  const handleChangePassword = () => {
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setShowPasswordModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditError('');
    setSuccessMessage('');

    try {
      let userEmail = localStorage.getItem('userEmail');
      let userRole = localStorage.getItem('userRole');

      console.log('Current localStorage data:', {
        userEmail,
        userRole,
        allKeys: Object.keys(localStorage)
      });

      // Fallback to default values if not in localStorage
      if (!userEmail || !userRole) {
        console.log('Using fallback values for email and role');
        userEmail = 'user@gmail.com';
        userRole = 'user';
      }

      const requestData = {
        email: userEmail,
        role: userRole,
        username: editForm.username,
        newEmail: editForm.email !== userEmail ? editForm.email : undefined
      };

      console.log('Sending edit profile request:', requestData);

      const response = await updateUserProfile(requestData);

      if (response.user) {
        setUserProfile(response.user);
        localStorage.setItem('userData', JSON.stringify(response.user));
        if (editForm.email !== userEmail) {
          localStorage.setItem('userEmail', editForm.email);
        }
        setSuccessMessage('Profile updated successfully!');
        setShowEditModal(false);
      }
    } catch (err) {
      console.error('Edit profile error:', err);
      setEditError(err.message || 'Failed to update profile');
    } finally {
      setEditLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError('');
    setSuccessMessage('');

    // Validate passwords
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      setPasswordLoading(false);
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      setPasswordLoading(false);
      return;
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordError('New password must be different from current password');
      setPasswordLoading(false);
      return;
    }

    try {
      let userEmail = localStorage.getItem('userEmail');
      let userRole = localStorage.getItem('userRole');

      console.log('Current localStorage data for password change:', {
        userEmail,
        userRole,
        allKeys: Object.keys(localStorage)
      });

      // Fallback to default values if not in localStorage
      if (!userEmail || !userRole) {
        console.log('Using fallback values for password change');
        userEmail = 'user@gmail.com';
        userRole = 'user';
      }

      const requestData = {
        email: userEmail,
        role: userRole,
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      };

      console.log('Sending password change request:', { ...requestData, currentPassword: '[HIDDEN]', newPassword: '[HIDDEN]' });

      const response = await changePassword(requestData);

      setSuccessMessage('Password changed successfully!');
      setShowPasswordModal(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      // Reset password visibility states
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    } catch (err) {
      console.error('Change password error:', err);
      setPasswordError(err.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Password strength checker
  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, color: '#ddd', text: '' };
    
    let strength = 0;
    if (password.length >= 6) strength += 1;
    if (password.length >= 8) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    
    const colors = ['#ff4444', '#ff8800', '#ffaa00', '#ffdd00', '#88ff00', '#00ff00'];
    const texts = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
    
    return {
      strength: Math.min(strength, 5),
      color: colors[Math.min(strength, 5)],
      text: texts[Math.min(strength, 5)]
    };
  };

  const passwordStrength = getPasswordStrength(passwordForm.newPassword);

  const copyToClipboard = async (text) => {
    try {
      setCopyStatus('copying');
      await navigator.clipboard.writeText(text);
      setCopyStatus('copied');
      setSuccessMessage('Copied to clipboard!');
      setTimeout(() => setCopyStatus('idle'), 2000);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Copy failed:', error);
      setCopyStatus('error');
      setTimeout(() => setCopyStatus('idle'), 2000);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingText}>Loading profile...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.title}>User Profile</h1>
          <button className={styles.backButton} onClick={handleBack}>
            Back to Dashboard
          </button>
        </div>

        <div className={styles.mainContent}>
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          {successMessage && (
            <div className={styles.successMessage}>
              {successMessage}
            </div>
          )}

          {!userProfile ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingText}>Profile not found</div>
            </div>
          ) : (
            <>
              {/* Profile Header */}
              <div className={styles.profileSection}>
                <div style={{
                  textAlign: 'center',
                  padding: '2rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '16px',
                  color: 'white',
                  marginBottom: '2rem'
                }}>
                  <div style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    margin: '0 auto 1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '3rem',
                    fontWeight: 'bold',
                    backdropFilter: 'blur(10px)',
                    border: '3px solid rgba(255, 255, 255, 0.3)'
                  }}>
                    {userProfile.username ? userProfile.username.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '2rem' }}>{userProfile.username}</h2>
                  <p style={{ margin: '0', opacity: 0.9, textTransform: 'capitalize' }}>{userProfile.role}</p>
                </div>
              </div>

              {/* Profile Information */}
              <div className={styles.profileSection}>
                <h3 className={styles.sectionTitle}>Profile Information</h3>
                
                <div className={styles.profileGrid}>
                  <div className={styles.profileInfo}>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>
                        <FaUser style={{ marginRight: '0.5rem' }} />
                        Username
                      </span>
                      <span className={styles.infoValue}>{userProfile.username}</span>
                    </div>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>
                        <FaEnvelope style={{ marginRight: '0.5rem' }} />
                        Email Address
                      </span>
                      <span className={styles.infoValue}>{userProfile.email}</span>
                    </div>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>
                        <FaIdCard style={{ marginRight: '0.5rem' }} />
                        User ID
                      </span>
                      <span className={styles.userIdValue}>
                        {userProfile.id}
                        <button 
                          className={`${styles.copyButton} ${copyStatus === 'copied' ? styles.copied : ''}`}
                          onClick={() => copyToClipboard(userProfile.id)}
                          title="Copy User ID"
                        >
                          {copyStatus === 'copied' ? <FaCheck /> : <FaCopy />}
                        </button>
                      </span>
                    </div>
                  </div>

                  <div className={styles.profileInfo}>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>
                        <FaCalendar style={{ marginRight: '0.5rem' }} />
                        Account Created
                      </span>
                      <span className={styles.infoValue}>
                        {userProfile.createdAt ? formatDate(userProfile.createdAt) : 'Recently created'}
                      </span>
                    </div>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>
                        <FaClock style={{ marginRight: '0.5rem' }} />
                        Last Updated
                      </span>
                      <span className={styles.infoValue}>
                        {userProfile.updatedAt ? formatDate(userProfile.updatedAt) : 'Recently updated'}
                      </span>
                    </div>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>
                        <FaIdCard style={{ marginRight: '0.5rem' }} />
                        Role
                      </span>
                      <span className={styles.infoValue} style={{ textTransform: 'capitalize' }}>
                        {userProfile.role}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className={styles.profileSection}>
                <h3 className={styles.sectionTitle}>Account Actions</h3>
                
                <div className={styles.actionButtons}>
                  <button className={styles.editButton} onClick={handleEditProfile}>
                    <FaEdit style={{ marginRight: '0.5rem' }} />
                    Edit Profile
                  </button>
                  
                  <button className={styles.passwordButton} onClick={handleChangePassword}>
                    <FaKey style={{ marginRight: '0.5rem' }} />
                    Change Password
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Edit Profile Modal */}
      {showEditModal && (
        <div className={styles.modal} onClick={() => setShowEditModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Edit Profile</h2>
              <button 
                className={styles.closeButton}
                onClick={() => setShowEditModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            
            {editError && (
              <div className={styles.modalError}>
                {editError}
              </div>
            )}

            <form onSubmit={handleEditSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  <FaUser style={{ marginRight: '0.5rem' }} />
                  Username
                </label>
                <input
                  type="text"
                  value={editForm.username}
                  onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                  className={styles.input}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  <FaEnvelope style={{ marginRight: '0.5rem' }} />
                  Email Address
                </label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className={styles.input}
                  required
                />
              </div>

              <div className={styles.modalButtons}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={editLoading}
                >
                  {editLoading ? 'Updating...' : 'Update Profile'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className={styles.modal} onClick={() => setShowPasswordModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Change Password</h2>
              <button 
                className={styles.closeButton}
                onClick={() => setShowPasswordModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            
            {passwordError && (
              <div className={styles.modalError}>
                {passwordError}
              </div>
            )}

            <form onSubmit={handlePasswordSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  <FaKey style={{ marginRight: '0.5rem' }} />
                  Current Password
                </label>
                <div className={styles.passwordInput}>
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    className={styles.input}
                    required
                    placeholder="Enter your current password"
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                  >
                    {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  <FaKey style={{ marginRight: '0.5rem' }} />
                  New Password
                </label>
                <div className={styles.passwordInput}>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className={styles.input}
                    required
                    minLength={6}
                    placeholder="Enter your new password (min 6 characters)"
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                  >
                    {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {passwordForm.newPassword && (
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
                      <div style={{ 
                        width: '100%', 
                        height: '4px', 
                        backgroundColor: '#ddd', 
                        borderRadius: '2px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${(passwordStrength.strength / 5) * 100}%`,
                          height: '100%',
                          backgroundColor: passwordStrength.color,
                          transition: 'all 0.3s ease'
                        }} />
                      </div>
                    </div>
                    <div style={{ 
                      fontSize: '0.875rem', 
                      color: passwordStrength.color,
                      fontWeight: 'bold'
                    }}>
                      {passwordStrength.text}
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  <FaKey style={{ marginRight: '0.5rem' }} />
                  Confirm New Password
                </label>
                <div className={styles.passwordInput}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    className={styles.input}
                    required
                    minLength={6}
                    placeholder="Confirm your new password"
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {passwordForm.confirmPassword && passwordForm.newPassword && (
                  <div style={{ 
                    marginBottom: '1rem',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    backgroundColor: passwordForm.newPassword === passwordForm.confirmPassword ? '#d4edda' : '#f8d7da',
                    color: passwordForm.newPassword === passwordForm.confirmPassword ? '#155724' : '#721c24',
                    fontSize: '0.875rem'
                  }}>
                    {passwordForm.newPassword === passwordForm.confirmPassword 
                      ? '✓ Passwords match' 
                      : '✗ Passwords do not match'
                    }
                  </div>
                )}
              </div>

              <div className={styles.modalButtons}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={() => setShowPasswordModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={passwordLoading}
                >
                  {passwordLoading ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile; 